import Component from '../../components/component/component';
import Icon from '../../components/icon/icon';
import Ajax from '../ajax/ajax';

import './tpl-calculator.scss';

interface ITplCalculatorCompany {
    key: string;
    premium: string;
    instalment: string;
}

interface ITplCalculatorSubmitResponse {
    success: boolean;
    errors?: string[];
    invalidFields?: string[];
    companies?: ITplCalculatorCompany[];
}

const LOADING_CLASS: string = 'is-loading';
const CURRENT_CLASS: string = 'is-current';
const COMPLETE_CLASS: string = 'is-complete';
const INVALID_CLASS: string = 'is-invalid';
const VISIBLE_CLASS: string = 'is-visible';

// step keys must match the data-panel/data-step attributes in tpl-calculator.twig
const STEPS: string[] = ['driver', 'vehicle', 'calculate'];

export default class TplCalculator extends Component {
    static initSelector: string = '.tpl-calculator__card';

    private section: JQuery;

    private submitXhr: JQueryXHR;

    private requiredMessage: string;
    private selectMessage: string;
    private requestMessage: string;
    private formatMessages: { [key: string]: string };

    constructor(target: HTMLElement) {
        super(target);

        this.requiredMessage = this.element.attr('data-field-error') || 'This field is required.';
        this.selectMessage = this.element.attr('data-select-error') || this.requiredMessage;
        this.requestMessage = this.element.attr('data-request-error') || 'Something went wrong, please try again.';
        // wrong-format messages per field name, used instead of the browser's own patternMismatch text
        this.formatMessages = {
            phone: this.element.attr('data-phone-error') || this.requiredMessage,
            /* eslint-disable-next-line @typescript-eslint/naming-convention */
            personal_id: this.element.attr('data-personal-id-error') || this.requiredMessage,
            /* eslint-disable-next-line @typescript-eslint/naming-convention */
            vehicle_plate: this.element.attr('data-plate-error') || this.requiredMessage,
        };

        this.section = this.element.closest('.tpl-calculator');

        this.element.find('.js-tpl-continue').on('click', this.handleContinue);
        this.element.find('.js-tpl-back').on('click', (event: JQuery.TriggeredEvent): void => this.goToStep($(event.currentTarget).data('back') as string));
        this.element.on('submit', this.handleSubmit);

        this.element.find('input, select').on('input change', (event: JQuery.TriggeredEvent): void => {
            this.clearFieldError(event.currentTarget as HTMLInputElement);
        });
    }

    private getCurrentStep(): string {
        return this.element.attr('data-current-step') || STEPS[0];
    }

    private goToStep(step: string): void {
        this.element.attr('data-current-step', step);

        this.element.find('.tpl-calculator__panel').removeClass(CURRENT_CLASS);
        this.element.find(`.tpl-calculator__panel[data-panel="${step}"]`).addClass(CURRENT_CLASS);

        this.setProgress(STEPS.indexOf(step));
        this.scrollToTop();
    }

    private showResult(): void {
        this.element.attr('data-current-step', 'result');
        this.element.find('.tpl-calculator__panel').removeClass(CURRENT_CLASS);
        this.element.find('.tpl-calculator__panel[data-panel="result"]').addClass(CURRENT_CLASS);

        this.setProgress(STEPS.length - 1);
        this.scrollToTop();
    }

    private setProgress(currentIndex: number): void {
        this.element.find('.tpl-calculator__progress-item').each((index: number, element: HTMLElement): void => {
            $(element)
                .toggleClass(COMPLETE_CLASS, index < currentIndex)
                .toggleClass(CURRENT_CLASS, index === currentIndex);
        });
    }

    private scrollToTop(): void {
        const target: JQuery = this.section.length ? this.section : this.element;
        const top: number = Math.max(0, target.offset().top - (window.gotoAndPlay.headerOffset || 0));

        $('html, body').stop(true).animate({ scrollTop: top }, 300);
    }

    private validatePanel(panel: string): boolean {
        const fields: JQuery = this.element.find(`.tpl-calculator__panel[data-panel="${panel}"]`).find('input, select');
        let isValid: boolean = true;

        fields.each((index: number, element: HTMLElement): void => {
            const input: HTMLInputElement = element as HTMLInputElement;
            const isEmptySelect: boolean = element.tagName === 'SELECT' && input.required && input.value === 'placeholder';

            if (!input.checkValidity() || isEmptySelect) {
                isValid = false;
                this.setFieldError(input, isEmptySelect);
            } else {
                this.clearFieldError(input);
            }
        });

        this.element.find(`.tpl-calculator__panel-error[data-error="${panel}"]`).toggleClass(VISIBLE_CLASS, !isValid);

        return isValid;
    }

    private setFieldError(input: HTMLInputElement, isEmptySelect: boolean = false): void {
        const wrapper: JQuery = $(input).closest('.textfield, .check');

        wrapper.addClass(INVALID_CLASS);

        if (wrapper.hasClass('check')) {
            this.renderCheckError(wrapper, this.requiredMessage);

            return;
        }

        let message: string = this.requiredMessage;
        const isWrongFormat: boolean = input.validity.patternMismatch || input.validity.tooShort || input.validity.tooLong;

        if (isEmptySelect) {
            message = this.selectMessage;
        } else if (isWrongFormat && this.formatMessages[input.name]) {
            message = this.formatMessages[input.name];
        } else if (!input.validity.valueMissing && input.validationMessage) {
            message = input.validationMessage;
        }

        let errorElement: JQuery = wrapper.children('.textfield__error');

        if (!errorElement.length) {
            errorElement = $('<div class="textfield__error" role="alert"></div>').appendTo(wrapper);
        }

        errorElement.text(message);
    }

    private renderCheckError(wrapper: JQuery, message: string): void {
        if (wrapper.children('.check__error').length) {
            return;
        }

        wrapper.append(
            `<div class="check__error" role="alert">${ Icon.render('error-outline', '', 'check__error-icon') }<span class="check__error-text">${ message }</span></div>`,
        );
    }

    private clearFieldError(input: HTMLInputElement): void {
        const wrapper: JQuery = $(input).closest('.textfield, .check');

        wrapper.removeClass(INVALID_CLASS);
        wrapper.children('.textfield__error, .check__error').remove();
    }

    private handleContinue: () => void = (): void => {
        const step: string = this.getCurrentStep();
        const nextStep: string = STEPS[STEPS.indexOf(step) + 1];

        if (!nextStep || !this.validatePanel(step)) {
            return;
        }

        this.goToStep(nextStep);
    };

    private handleSubmit: (event: JQuery.TriggeredEvent) => void = (event: JQuery.TriggeredEvent): void => {
        event.preventDefault();

        if (!this.validatePanel('calculate') || this.submitXhr && this.submitXhr.state() === 'pending') {
            return;
        }

        const formData: FormData = new FormData(this.element[0] as HTMLFormElement);

        formData.append('action', 'tpl-calculator-submit');
        formData.append('source_url', window.location.href);

        this.element.addClass(LOADING_CLASS);

        this.submitXhr = Ajax.post(formData, { contentType: false });
        this.submitXhr.done((response: ITplCalculatorSubmitResponse): void => {
            if (response.success && response.companies) {
                this.renderResult(response);
                this.showResult();
            } else {
                this.showErrors(response.errors || [], response.invalidFields || []);
            }
        });
        this.submitXhr.fail((jqXHR: JQueryXHR, textStatus: string): void => {
            if (textStatus !== 'abort') {
                this.showErrors([this.requestMessage], []);
            }
        });
        this.submitXhr.always((): void => {
            this.element.removeClass(LOADING_CLASS);
        });
    };

    private renderResult(response: ITplCalculatorSubmitResponse): void {
        (response.companies || []).forEach((company: ITplCalculatorCompany): void => {
            const row: JQuery = this.element.find(`.tpl-calculator__company[data-company="${company.key}"]`);

            row.find('.js-tpl-premium').text(company.premium);
            row.find('.js-tpl-instalment').text(company.instalment);
        });
    }

    private showErrors(errors: string[], invalidFields: string[]): void {
        let panelIndex: number = STEPS.indexOf('calculate');

        invalidFields.forEach((name: string): void => {
            const input: JQuery = this.element.find(`[name="${name}"]`);
            const wrapper: JQuery = input.closest('.textfield, .check');

            wrapper.addClass(INVALID_CLASS);

            if (wrapper.hasClass('check')) {
                this.renderCheckError(wrapper, this.requiredMessage);
            }

            const fieldIndex: number = STEPS.indexOf(input.closest('.tpl-calculator__panel').data('panel') as string);

            if (fieldIndex > -1 && fieldIndex < panelIndex) {
                panelIndex = fieldIndex;
            }
        });

        const panel: string = STEPS[panelIndex];

        if (panel !== this.getCurrentStep()) {
            this.goToStep(panel);
        }

        const errorPanel: JQuery = this.element.find(`.tpl-calculator__panel-error[data-error="${panel}"]`);

        errorPanel.find('.alert__content').text(errors.join(' '));
        errorPanel.addClass(VISIBLE_CLASS);
    }
}
